import type { Component, JSXElement, ParentComponent } from "solid-js";
import { createMemo, Show, Suspense } from "solid-js";
import EventAnalytics from "~/js/utilities/EventAnalytics";
import { PaymentMethodModel, PaymentMethodUi } from "~/solidJs/entities/PaymentMethod";
import { Button, ModalV2Ui } from "~/solidJs/shared";
import {
	type CalculateAddMembersCostResponse,
	DurationMonths,
	type TeamPricingResponse
} from "~/solidJs/shared/api/TeamApi";
import { formatCurrency } from "~/solidJs/shared/helpers/currencyHelpers";
import { formatDateMonthDayYear } from "~/solidJs/shared/helpers/dateHelpers";
import { Divider } from "~/solidJs/shared/ui/atoms/Divider/Divider";
import type { TeamSeatsModalConfig, TeamSeatsModalState } from "../methods/teamSeatsModal";
import {
	TeamSeatsModalContext,
	useTeamSeatsModal,
	useTeamSeatsModalContext
} from "../methods/teamSeatsModal";
import { BillingPlanSelector } from "./BillingPlanSelector";

// Helper functions to work with both response types
const isAddMembersCostResponse = (
	data: TeamPricingResponse | CalculateAddMembersCostResponse | undefined
): data is CalculateAddMembersCostResponse => {
	return data !== undefined && "prorated_cost" in data && "total_cost" in data;
};

const getPrice = (
	data: TeamPricingResponse | CalculateAddMembersCostResponse | undefined
): string => {
	if (!data) return "0";
	if (isAddMembersCostResponse(data)) {
		return data.prorated_cost.toString();
	}
	return data.price;
};

const getDiscountPrice = (
	data: TeamPricingResponse | CalculateAddMembersCostResponse | undefined
): string | null => {
	if (!data) return null;
	if (isAddMembersCostResponse(data)) {
		return null; // Add members cost doesn't have discount
	}
	return data.discount_price ?? null;
};

const getPricePerSeat = (
	data: TeamPricingResponse | CalculateAddMembersCostResponse | undefined
): string => {
	if (!data) return "0";
	if (isAddMembersCostResponse(data)) {
		// Calculate average price per seat from new_members_costs
		if (data.new_members_costs.length > 0) {
			return data.new_members_costs[0].cost_per_seat.toString();
		}
		return "0";
	}
	return data.price_per_seat;
};

const getNextBillingDate = (
	data: TeamPricingResponse | CalculateAddMembersCostResponse | undefined
): string | null => {
	if (!data) return null;
	if (isAddMembersCostResponse(data)) {
		return data.next_billing_date ?? null;
	}
	return data.next_billing_date;
};

const getNextBillingPrice = (
	data: TeamPricingResponse | CalculateAddMembersCostResponse | undefined
): string | null => {
	if (!data) return null;
	if (isAddMembersCostResponse(data)) {
		return data.next_billing_price?.toString() ?? null;
	}
	// For regular pricing, use discount_price or price
	return data.discount_price ?? data.price;
};

type NamedDetailItemProps = {
	label: JSXElement;
	detailElement: JSXElement;
	alignCenter?: boolean;
};

const NamedDetailItem: Component<NamedDetailItemProps> = (props) => {
	return (
		<div class={`flex w-full ${props.alignCenter ? "items-center" : ""}`}>
			<div class="typography-text-14-medium flex-1 text-text-secondary-v2">{props.label}</div>
			<div class="flex flex-1 shrink-0 justify-end">{props.detailElement}</div>
		</div>
	);
};

// Skeleton for price loading state
const PriceSkeleton: Component = () => (
	<div class="flex flex-col items-end gap-1">
		<div class="h-8 w-16 animate-pulse rounded bg-background-gray-light" />
		<div class="h-3 w-12 animate-pulse rounded bg-background-gray-light" />
	</div>
);

// Skeleton for billing info loading state
const BillingInfoSkeleton: Component = () => (
	<div class="flex flex-col items-end gap-1">
		<div class="h-5 w-24 animate-pulse rounded bg-background-gray-light" />
		<div class="h-3 w-16 animate-pulse rounded bg-background-gray-light" />
	</div>
);

const TotalDuePrice: Component<{ state: TeamSeatsModalState }> = (props) => {
	const query = () => props.state.teamPricingQuery();
	const hasData = createMemo(() => !query().isFetching && query().data !== undefined);

	return (
		<Show when={hasData()} fallback={<PriceSkeleton />}>
			<div class="flex flex-col items-end gap-1">
				<div class="flex items-end gap-2">
					<Show when={getDiscountPrice(query().data)}>
						<span class="mb-[3px] whitespace-nowrap text-2xs text-text-secondary-v2 leading-[14px] line-through">
							{formatCurrency(
								getPrice(query().data),
								props.state.getSelectedCurrency()
							)}
						</span>
					</Show>
					<span class="whitespace-nowrap text-right font-medium text-2xl text-interaction-black leading-8">
						{formatCurrency(
							getDiscountPrice(query().data) ?? getPrice(query().data),
							props.state.getSelectedCurrency()
						)}
					</span>
				</div>
				<span class="label-10-regular text-text-tertiary">
					{formatCurrency(
						getPricePerSeat(query().data),
						props.state.getSelectedCurrency()
					)}
					/seat
				</span>
			</div>
		</Show>
	);
};

const NextBillingInfo: Component<{ state: TeamSeatsModalState }> = (props) => {
	const query = () => props.state.teamPricingQuery();
	const hasData = createMemo(() => !query().isFetching && query().data !== undefined);

	const formattedDate = createMemo(() => {
		const data = query().data;
		if (!data) return null;
		const dateStr = getNextBillingDate(data);
		if (!dateStr) return null;
		try {
			return formatDateMonthDayYear(new Date(dateStr));
		} catch {
			return null;
		}
	});

	const billingPeriodLabel = createMemo(() => {
		const data = query().data;
		if (!data) return "monthly";
		if (isAddMembersCostResponse(data)) {
			return data.subscription_type || "monthly";
		}
		return props.state.durationMonths() === DurationMonths.Yearly ? "annually" : "monthly";
	});

	const nextBillingPrice = createMemo(() => {
		const data = query().data;
		return data ? getNextBillingPrice(data) : null;
	});

	return (
		<Show when={hasData()} fallback={<BillingInfoSkeleton />}>
			<div class="flex flex-col items-end gap-px">
				<Show when={formattedDate()}>
					<span class="typography-text-14-semibold text-text-primary">
						{formattedDate()}
					</span>
				</Show>
				<span class="label-10-regular text-text-tertiary">
					{formatCurrency(nextBillingPrice() ?? "0", props.state.getSelectedCurrency())}{" "}
					{billingPeriodLabel()}
				</span>
			</div>
		</Show>
	);
};

// Skeleton for payment selector loading state
const PaymentSkeleton: Component = () => (
	<div class="h-9 w-40 animate-pulse rounded-lg bg-background-gray-light" />
);

const PaymentInfo: Component<{
	state: TeamSeatsModalState;
	isCreating?: boolean;
	isAddingSeats?: boolean;
}> = (props) => {
	return (
		<Suspense fallback={<PaymentSkeleton />}>
			<PaymentMethodUi.PaymentSelector
				selectedPaymentMethod={props.state.getSelectedPaymentMethod()}
				paymentMethods={props.state.transformedPaymentMethod() || []}
				onChange={(d) => {
					props.state.setSelectPaymentMethod(d.items[0]);
					if (props.isCreating) {
						const system = d.items[0]?.system;
						if (system) {
							EventAnalytics.team.createTeam.paymentMethodSelected(system);
						}
					}
				}}
				onClick={() => {
					if (props.isCreating) {
						EventAnalytics.team.createTeam.paymentMethodClick();
					} else if (props.isAddingSeats) {
						EventAnalytics.team.addSeats.paymentMethodClick();
					}
				}}
			/>
		</Suspense>
	);
};

const CurrencyInfo: Component<{ state: TeamSeatsModalState }> = (props) => {
	// Для "Add seats": блокируем валюту если выбран сохраненный метод со Stripe
	const isReadonly = createMemo(() => {
		if (!props.state.isAddingSeats) {
			return false;
		}

		const selectedMethod = props.state.getSelectedPaymentMethod()[0];
		// Если выбран "Add new payment method", разрешаем выбор валюты
		if (selectedMethod?.id === PaymentMethodModel.ADD_NEW_PAYMENT_METHOD_ID) {
			return false;
		}

		// Если выбран сохраненный метод, проверяем его систему
		// Для Stripe блокируем (валюта привязана к методу), для YooKassa разрешаем
		return selectedMethod?.system === "stripe";
	});

	return (
		<PaymentMethodUi.CurrencySelector
			hideLabel
			currencies={PaymentMethodModel.PRICING_PAGE_CURRENCIES}
			selectedCurrency={props.state.selectedCurrency()}
			onChange={props.state.setSelectedCurrency}
			readonly={isReadonly()}
		/>
	);
};

const ContactSalesInfo: Component<{ seats: number; isCreating?: boolean }> = (props) => {
	return (
		<div class="flex w-full items-center gap-2.5 rounded-lg bg-background-gray-light px-2 py-2">
			<div class="relative size-4 shrink-0">
				<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
					<path
						d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm1 12H7V7h2v5zm0-6H7V4h2v2z"
						fill="currentColor"
					/>
				</svg>
			</div>
			<div class="caption-11-regular flex-1 text-text-secondary-v2">
				You've reached the limit for the Teams plan (10 members). Want more?{" "}
				<a
					href="https://airtable.com/appqIlvnjfPjskCLX/pagDwW9FSJDfNKxqH/form"
					target="_blank"
					rel="noopener noreferrer"
					class="caption-12-semibold text-interaction-violet underline"
					onClick={() => {
						if (props.isCreating) {
							EventAnalytics.team.createTeam.contactManagerClick(props.seats);
						}
					}}
				>
					Contact our manager
				</a>
			</div>
		</div>
	);
};

const TeamSeatsModalContent: Component = () => {
	const methods = useTeamSeatsModalContext();

	// Мемоизируем state объект чтобы избежать пересоздания при каждом рендере
	const state = createMemo<TeamSeatsModalState>(() => ({
		seats: methods.seats,
		teamName: methods.teamName,
		teamPricingQuery: methods.teamPricingQuery,
		getSelectedPaymentMethod: methods.getSelectedPaymentMethod,
		getSelectedPaymentMethodWithoutNew: methods.getSelectedPaymentMethodWithoutNew,
		selectedCurrency: methods.selectedCurrency,
		setSelectedCurrency: methods.setSelectedCurrency,
		setSelectPaymentMethod: methods.setSelectPaymentMethod,
		transformedPaymentMethod: methods.transformedPaymentMethod,
		getSelectedCurrency: methods.getSelectedCurrency,
		durationMonths: methods.durationMonths,
		setDurationMonths: methods.setDurationMonths,
		isAddingSeats: methods.config.isAddingSeats || false
	}));

	const showContactSales = () => methods.config.showContactSales(state());
	const showBillingSelector = () => methods.config.showBillingPlanSelector !== false;

	const handlePayment = () => {
		const isCreating = !methods.config.isAddingSeats && !methods.config.isReactivating;
		if (isCreating) {
			const query = methods.teamPricingQuery();
			const data = query.data;
			const price = data
				? "discount_price" in data && data.discount_price
					? data.discount_price
					: "price" in data
						? data.price
						: "0"
				: "0";
			EventAnalytics.team.createTeam.confirm({
				team_name: methods.teamName(),
				seats_count: Number.parseInt(methods.seats(), 10) || 2,
				billing_plan:
					methods.durationMonths() === DurationMonths.Yearly ? "yearly" : "monthly",
				total_amount: Number.parseFloat(price)
			});
		}
		methods.Form.handleSubmit();
	};

	return (
		<ModalV2Ui.ModalWithOverlayBoilerPlate
			toggleSelf={methods.config.onClose}
			class="flex-col justify-start"
			autoClose
		>
			<ModalV2Ui.Header toggleSelf={methods.config.onClose} class="px-5 py-4">
				<ModalV2Ui.HeaderTextContainer>
					<h2 class="header-30-semibold text-text-primary">{methods.config.title}</h2>
					<p class="typography-text-14-medium text-text-secondary-v2">
						{methods.config.subtitle}
					</p>
				</ModalV2Ui.HeaderTextContainer>
			</ModalV2Ui.Header>
			<div class="flex w-full flex-col gap-1">
				<ModalV2Ui.ContentContainer class="flex-col gap-4 pb-5">
					<methods.config.FormInputs />
					<Show
						when={!showContactSales()}
						fallback={
							<ContactSalesInfo
								seats={Number.parseInt(methods.seats(), 10) || 2}
								isCreating={
									!methods.config.isAddingSeats && !methods.config.isReactivating
								}
							/>
						}
					>
						<Show when={showBillingSelector()}>
							<BillingPlanSelector
								selectedDuration={methods.durationMonths()}
								onDurationChange={methods.setDurationMonths}
								seats={Number.parseInt(methods.seats(), 10) || 2}
								yearlyPrice={getPrice(methods.yearlyPricingQuery.data)}
								yearlyDiscountPrice={
									getDiscountPrice(methods.yearlyPricingQuery.data) ?? undefined
								}
								yearlyPricePerSeat={getPricePerSeat(
									methods.yearlyPricingQuery.data
								)}
								monthlyPrice={getPrice(methods.monthlyPricingQuery.data)}
								monthlyDiscountPrice={
									getDiscountPrice(methods.monthlyPricingQuery.data) ?? undefined
								}
								monthlyPricePerSeat={getPricePerSeat(
									methods.monthlyPricingQuery.data
								)}
								isLoading={
									methods.yearlyPricingQuery.isFetching ||
									methods.monthlyPricingQuery.isFetching
								}
								currency={methods.getSelectedCurrency()}
								onYearlySelected={() => {
									if (
										!methods.config.isAddingSeats &&
										!methods.config.isReactivating
									) {
										EventAnalytics.team.createTeam.yearlyPlanSelected();
									}
								}}
								onMonthlySelected={() => {
									if (
										!methods.config.isAddingSeats &&
										!methods.config.isReactivating
									) {
										EventAnalytics.team.createTeam.monthlyPlanSelected();
									}
								}}
							/>
						</Show>
					</Show>
				</ModalV2Ui.ContentContainer>
				<Show when={!showContactSales()}>
					<div class="px-6">
						<Divider />
					</div>
					<ModalV2Ui.ContentContainer class="w-full flex-col gap-4 pt-4 pb-4">
						<NamedDetailItem
							label="Total due"
							detailElement={<TotalDuePrice state={state()} />}
						/>
						<NamedDetailItem
							label="Next billing"
							detailElement={<NextBillingInfo state={state()} />}
						/>
						<NamedDetailItem
							label="Payment"
							detailElement={
								<PaymentInfo
									state={state()}
									isCreating={
										!methods.config.isAddingSeats &&
										!methods.config.isReactivating
									}
									isAddingSeats={methods.config.isAddingSeats}
								/>
							}
							alignCenter
						/>
						<Show
							when={
								state().getSelectedPaymentMethod()[0]?.id ===
								PaymentMethodModel.ADD_NEW_PAYMENT_METHOD_ID
							}
						>
							<NamedDetailItem
								label="Pay in"
								detailElement={<CurrencyInfo state={state()} />}
								alignCenter
							/>
						</Show>
					</ModalV2Ui.ContentContainer>
				</Show>
			</div>
			<ModalV2Ui.ButtonContainer class="p-4">
				<Button
					onClick={() => {
						const isCreating =
							!methods.config.isAddingSeats && !methods.config.isReactivating;
						if (isCreating) {
							EventAnalytics.team.createTeam.cancel();
						}
						methods.config.onClose();
					}}
					variant="TertiaryAccent"
					size="large"
				>
					Cancel
				</Button>
				<Show
					when={showContactSales()}
					fallback={
						<Button
							pending={methods.isSubmitting()}
							onClick={handlePayment}
							variant="PrimaryAccent"
							size="large"
						>
							{methods.config.buttonText || "Go to Payment"}
						</Button>
					}
				>
					<Button
						variant="PrimaryAccent"
						component="a"
						href="https://airtable.com/appqIlvnjfPjskCLX/pagDwW9FSJDfNKxqH/form"
						target="_blank"
						size="large"
						onClick={() => {
							const isCreating =
								!methods.config.isAddingSeats && !methods.config.isReactivating;
							if (isCreating) {
								EventAnalytics.team.createTeam.contactSalesClick();
							}
						}}
					>
						Contact Sales
					</Button>
				</Show>
			</ModalV2Ui.ButtonContainer>
		</ModalV2Ui.ModalWithOverlayBoilerPlate>
	);
};

export const TeamSeatsModalProvider: ParentComponent<{ config: TeamSeatsModalConfig }> = (
	props
) => {
	const methods = useTeamSeatsModal(props.config);
	return (
		<TeamSeatsModalContext.Provider value={methods}>
			{props.children}
		</TeamSeatsModalContext.Provider>
	);
};

export const TeamSeatsModal: Component<{ config: TeamSeatsModalConfig }> = (props) => {
	return (
		<TeamSeatsModalProvider config={props.config}>
			<TeamSeatsModalContent />
		</TeamSeatsModalProvider>
	);
};
